import classNames from "classnames";
import React, { useRef, FC, useState } from "react";

import { ReactComponent as CompassImage } from "./images/compass.svg";
import IconButton from "../IconButton/IconButton";

import styles from "./Panorama.module.scss";
import Spinner from "../Spinner/Spinner";
import BuildingBlock from "./components/BuildingBlock/BuildingBlock";

import useOrientationControls from "./hooks/useOrientationControls";
import useZoomControl from "./hooks/useZoomControl";
import usePanoramaSetup from "./hooks/usePanoramaSetup";

interface Props {
  dataQA: string;
  className?: string;
  imageUrl: string;
  onClose?: () => void;
  buildingBlockLabel?: string;
}

const PanoramicViewer: FC<Props> = ({
  imageUrl,
  dataQA,
  className,
  onClose,
  buildingBlockLabel = "Drag to look around",
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const compassRef = useRef<HTMLDivElement>(null); // Ref for the compass element
  const isZooming = useRef(false);
  const [useOrientation, setUseOrientation] = useState(false);

  const { cameraRef, loaded, userHasInteracted, lon, resetView } =
    usePanoramaSetup({
      mountRef,
      imageUrl,
      useOrientation,
      isZooming,
      compassRef,
    });

  const { requestPermission, disableOrientation, supportsOrientationControl } =
    useOrientationControls({
      cameraRef,
      lonRef: lon,
      setUseOrientation,
    });

  useZoomControl({ cameraRef, mountRef, isZooming });

  return (
    <div
      data-qa={dataQA}
      ref={mountRef}
      className={classNames(styles.container, className)}
      style={{ touchAction: "none" }}
    >
      {!loaded && (
        <div className={styles.loading}>
          <Spinner dataQA="panorama-loading" type="BeatLoader" color="#FFF" />
        </div>
      )}
      {loaded && (
        <>
          {!userHasInteracted && (
            <BuildingBlock
              icon="HandIcon"
              dataQA="drag-notice"
              text={buildingBlockLabel}
              className={styles.dragNotice}
            />
          )}
          <IconButton
            dataQA="close-panorama"
            icon="CloseIcon"
            onClick={() => onClose && onClose()}
            className={styles.closeButton}
          />
          {supportsOrientationControl() && (
            <IconButton
              dataQA="enable-gyroscope"
              icon="GryoscopeIcon"
              onClick={() =>
                useOrientation ? disableOrientation() : requestPermission()
              }
              className={classNames(styles.gyroscopeButton, {
                [styles.enabled]: useOrientation,
              })}
              largeIcon
            />
          )}
          <div ref={compassRef} className={styles.compass} onClick={resetView}>
            <CompassImage />
          </div>
        </>
      )}
    </div>
  );
};

export default PanoramicViewer;
